'use client';

import React, { useState, useMemo, useEffect } from 'react';
import { Filter, X, CheckCircle2, Clock, AlertTriangle, Plus, Trash2 } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';
import { getCosmeticItems, saveCosmeticItems, CosmeticItem, CheckStatus } from '@/lib/cosmeticStore';

const DEFAULT_CATEGORIES = [
  'Body Panel',
  'Glass / Windscreen',
  'Lights',
  'Interior',
  'Tyres / Wheels',
  'Undercarriage',
  'Other',
];

const STATUS_CONFIG: Record<CheckStatus, { label: string; color: string; bg: string }> = {
  pass: { label: 'Pass', color: 'text-emerald-400', bg: 'bg-emerald-400/10 border-emerald-400/30' },
  fail: { label: 'Fail', color: 'text-destructive', bg: 'bg-destructive/10 border-destructive/20' },
  na: { label: 'N/A', color: 'text-muted-foreground', bg: 'bg-secondary border-border' },
  '': { label: 'Pending', color: 'text-amber-400', bg: 'bg-amber-400/10 border-amber-400/20' },
};

interface AddItemFormState {
  vehicleReg: string;
  category: string;
  customCategory: string;
  description: string;
}

export default function CosmeticChecklistClient() {
  const [items, setItems] = useState<CosmeticItem[]>([]);
  const [regSearch, setRegSearch] = useState('');
  const [regLookup, setRegLookup] = useState('');
  const [showFilter, setShowFilter] = useState<'all' | 'pending' | 'completed'>('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState<AddItemFormState>({
    vehicleReg: '',
    category: DEFAULT_CATEGORIES[0],
    customCategory: '',
    description: '',
  });
  const [formError, setFormError] = useState('');

  // Load persisted items on mount
  useEffect(() => {
    setItems(getCosmeticItems());
  }, []);

  // Helper to update state and persist
  const updateItems = (next: CosmeticItem[]) => {
    setItems(next);
    saveCosmeticItems(next);
  };

  // Update a specific field on an item
  const handleStatusChange = (id: string, status: CheckStatus) => {
    updateItems(
      items.map((item) =>
        item.id === id
          ? {
              ...item,
              status,
              completed: status === 'pass' || status === 'na',
            }
          : item
      )
    );
  };

  const handleNotesChange = (id: string, notes: string) => {
    updateItems(items.map((item) => (item.id === id ? { ...item, notes } : item)));
  };

  const handleDelete = (id: string) => {
    updateItems(items.filter((item) => item.id !== id));
  };

  const handleAddItem = (e: React.FormEvent) => {
    e.preventDefault();
    const reg = form.vehicleReg.trim().toUpperCase();
    const desc = form.description.trim();
    const cat = form.category === '__custom__' ? form.customCategory.trim() : form.category;
    if (!reg) { setFormError('Vehicle registration is required'); return; }
    if (!desc) { setFormError('Description is required'); return; }
    if (!cat) { setFormError('Category is required'); return; }
    const newItem: CosmeticItem = {
      id: `ci-${Date.now()}`,
      vehicleReg: reg,
      category: cat,
      description: desc,
      status: '',
      notes: '',
      completed: false,
      reportedDate: new Date().toISOString().split('T')[0],
    };
    updateItems([...items, newItem]);
    setForm({ vehicleReg: reg, category: DEFAULT_CATEGORIES[0], customCategory: '', description: '' });
    setFormError('');
    setShowAddForm(false);
  };

  const handleLookup = () => {
    const trimmed = regSearch.trim().toUpperCase();
    setRegLookup(trimmed);
    setShowFilter('all');
  };

  const handleClearLookup = () => {
    setRegSearch('');
    setRegLookup('');
  };

  const filtered = useMemo(() => {
    let result = [...items];
    if (regLookup) result = result.filter((i) => i.vehicleReg.includes(regLookup));
    if (showFilter === 'pending') result = result.filter((i) => !i.completed);
    if (showFilter === 'completed') result = result.filter((i) => i.completed);
    return result;
  }, [items, regLookup, showFilter]);

  const pendingCount = items.filter((i) => !i.completed).length;
  const completedCount = items.filter((i) => i.completed).length;
  const failCount = items.filter((i) => i.status === 'fail').length;

  const groupedByVehicle = useMemo(() => {
    return filtered.reduce<Record<string, CosmeticItem[]>>((acc, item) => {
      if (!acc[item.vehicleReg]) acc[item.vehicleReg] = [];
      acc[item.vehicleReg].push(item);
      return acc;
    }, {});
  }, [filtered]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Cosmetic Checklist</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Inspect and track vehicle cosmetic defects with Pass / Fail / N/A status
          </p>
        </div>
        <button
          onClick={() => setShowAddForm((v) => !v)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors"
        >
          <Plus size={16} />
          Add Checklist Item
        </button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total Items', value: items.length, icon: Filter, color: 'text-primary' },
          { label: 'Pending', value: pendingCount, icon: Clock, color: 'text-amber-400' },
          { label: 'Completed', value: completedCount, icon: CheckCircle2, color: 'text-emerald-400' },
          { label: 'Failures', value: failCount, icon: AlertTriangle, color: 'text-destructive' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="card-surface border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <Icon size={14} className={color} />
              <span className="text-xs text-muted-foreground">{label}</span>
            </div>
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Add Item Form */}
      {showAddForm && (
        <div className="card-surface border border-border rounded-xl p-5">
          <h2 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
            <Plus size={16} className="text-primary" />
            New Checklist Item
          </h2>
          <form onSubmit={handleAddItem} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label-field">Vehicle Registration <span className="text-destructive">*</span></label>
                <input
                  type="text"
                  value={form.vehicleReg}
                  onChange={(e) => setForm((f) => ({ ...f, vehicleReg: e.target.value.toUpperCase() }))}
                  className="input-field font-mono-data uppercase"
                  placeholder="e.g. GP 23-441"
                />
              </div>
              <div>
                <label className="label-field">Category <span className="text-destructive">*</span></label>
                <select
                  value={form.category}
                  onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                  className="input-field"
                >
                  {DEFAULT_CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                  <option value="__custom__">+ Custom category…</option>
                </select>
              </div>
              {form.category === '__custom__' && (
                <div className="sm:col-span-2">
                  <label className="label-field">Custom Category Name <span className="text-destructive">*</span></label>
                  <input
                    type="text"
                    value={form.customCategory}
                    onChange={(e) => setForm((f) => ({ ...f, customCategory: e.target.value }))}
                    className="input-field"
                    placeholder="Enter custom category name"
                  />
                </div>
              )}
              <div className="sm:col-span-2">
                <label className="label-field">Defect / Item Description <span className="text-destructive">*</span></label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  className="input-field"
                  placeholder="e.g. Scratch on front left door panel"
                />
              </div>
            </div>
            {formError && <p className="field-error">{formError}</p>}
            <div className="flex gap-3 pt-1">
              <button
                type="submit"
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors"
              >
                <Plus size={14} />
                Add Item
              </button>
              <button
                type="button"
                onClick={() => { setShowAddForm(false); setFormError(''); }}
                className="px-4 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filters */}
      <div className="card-surface border border-border rounded-xl p-4 space-y-3">
        <div className="flex gap-2">
          <input
            type="text"
            value={regSearch}
            onChange={(e) => setRegSearch(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
            placeholder="Search by registration (e.g. GP 23-441)"
            className="input-field flex-1 font-mono-data uppercase text-sm"
          />
          <button
            onClick={handleLookup}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            Search
          </button>
          {regLookup && (
            <button
              onClick={handleClearLookup}
              className="px-3 py-2 rounded-lg bg-secondary border border-border text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <div className="flex gap-1 p-1 bg-secondary rounded-lg w-fit">
          {(['all', 'pending', 'completed'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setShowFilter(f)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors capitalize ${
                showFilter === f ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
        {regLookup && (
          <div className="flex items-center gap-2 flex-wrap">
            <Filter size={12} className="text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Filtering by:</span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 border border-primary/20 text-xs text-primary font-mono-data">
              {regLookup}
              <button onClick={handleClearLookup} className="ml-0.5 hover:text-primary/60"><X size={10} /></button>
            </span>
            <span className="text-xs text-muted-foreground ml-auto">{filtered.length} item{filtered.length !== 1 ? 's' : ''} shown</span>
          </div>
        )}
      </div>

      {/* Items */}
      {filtered.length === 0 ? (
        <div className="card-surface border border-border rounded-xl p-12 text-center">
          <CheckCircle2 size={40} className="text-muted-foreground mx-auto mb-3 opacity-40" />
          <p className="text-muted-foreground text-sm">
            {items.length === 0
              ? 'No checklist items yet. Click "Add Checklist Item" to begin.' :'No items match your current filters.'}
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          {Object.entries(groupedByVehicle).map(([reg, vehicleItems]) => {
            const pendingVehicle = vehicleItems.filter((i) => !i.completed).length;
            const completedVehicle = vehicleItems.filter((i) => i.completed).length;
            return (
              <div key={reg} className="card-surface border border-border rounded-xl overflow-hidden">
                {/* Vehicle header */}
                <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-secondary/40">
                  <p className="text-sm font-bold text-foreground tracking-wide font-mono-data">{reg}</p>
                  <div className="flex items-center gap-2">
                    {pendingVehicle > 0 && (
                      <span className="px-2 py-0.5 rounded-full bg-amber-400/10 border border-amber-400/20 text-xs text-amber-400 font-medium">
                        {pendingVehicle} pending
                      </span>
                    )}
                    {completedVehicle > 0 && (
                      <span className="px-2 py-0.5 rounded-full bg-emerald-400/10 border border-emerald-400/20 text-xs text-emerald-400 font-medium">
                        {completedVehicle} done
                      </span>
                    )}
                  </div>
                </div>

                {/* Items list */}
                <div className="divide-y divide-border">
                  {vehicleItems.map((item) => {
                    const statusCfg = STATUS_CONFIG[item.status];
                    return (
                      <div key={item.id} className="px-5 py-4 space-y-3">
                        {/* Top row: category + description + delete */}
                        <div className="flex items-start gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{item.category}</span>
                              <span className="text-xs text-muted-foreground">·</span>
                              <span className="text-xs text-muted-foreground">{item.reportedDate}</span>
                            </div>
                            <p className="text-sm text-foreground">{item.description}</p>
                          </div>
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors flex-shrink-0"
                            title="Remove item"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>

                        {/* Status selector */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs text-muted-foreground mr-1">Status:</span>
                          {(['pass', 'fail', 'na'] as CheckStatus[]).map((s) => {
                            const cfg = STATUS_CONFIG[s];
                            return (
                              <button
                                key={s}
                                onClick={() => handleStatusChange(item.id, item.status === s ? '' : s)}
                                className={`px-3 py-1 rounded-full border text-xs font-semibold transition-colors ${
                                  item.status === s
                                    ? `${cfg.bg} ${cfg.color}`
                                    : 'bg-secondary border-border text-muted-foreground hover:text-foreground'
                                }`}
                              >
                                {cfg.label}
                              </button>
                            );
                          })}
                          {item.status && (
                            <span className={`ml-auto text-xs font-medium ${statusCfg.color}`}>
                              {statusCfg.label}
                            </span>
                          )}
                        </div>

                        {/* Notes field */}
                        <div>
                          <input
                            type="text"
                            value={item.notes}
                            onChange={(e) => handleNotesChange(item.id, e.target.value)}
                            className="input-field text-sm"
                            placeholder="Notes / Damage description (optional)"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
